import React from 'react'
import type { GetStaticPaths, GetStaticProps } from 'next'
import {
  Container,
  PaginationEllipsis,
  PaginationLinks,
  textStyles,
} from '@marketing-sites/ui'
import Head from 'next/head'
import type { ArticlesQueryQuery } from '../../../.graphql/generated-types'
import { ArticlesQueryDocument } from '../../../.graphql/generated-types'
import {
  notEmpty,
  calculatePageCount,
  extractPageNumberFromParam,
  pageSize,
  siteLocale,
} from '@marketing-sites/utils'
import { getGraphQLClient, graphQLClient } from '../../lib/api'
import { ArticleListItem } from '../ArticleListItem'
import { FeaturedArticleListItem } from '../../components/FeaturedArticleListItem'
import { ComposeComponentTopicList } from '../ComposeComponentTopicList'

import {
  ArticlePaginationLink,
  ArticlePaginationNextLink,
  ArticlePaginationPrevLink,
} from '../ArticlePagination'
import type { Substitutions } from '../../components/ContentfulRichText'
import { fetchGlobalRegionalEditsAsSubstitutions } from '../GlobalRegionalEdits'
import { ARTICLES_PAGES_REVALIDATE_SECONDS } from '../articlesRevalidate'
import {
  buildLocalizedVersions,
  buildURLForLocale,
  getXDefaultHref,
} from '../ArticleHead/hreflang'

export interface ArticleIndexPageProps {
  kind: 'ArticleIndexPageProps'
  data?: ArticlesQueryQuery | null
  substitutions: Substitutions
  pageNumber: number
}

export function ArticlesIndexPage({
  data,
  substitutions,
  pageNumber,
}: ArticleIndexPageProps) {
  const articleItems =
    data?.composePageArticleCollection?.items.filter(notEmpty)

  const latestArticles = articleItems?.map((articleListItemData) => (
    <li
      key={articleListItemData.sys.id}
      className="bg-white hover:shadow-[0px_0px_10px_rgba(0,0,0,0.16)]"
    >
      <ArticleListItem
        data={articleListItemData}
        substitutions={substitutions}
      />{' '}
    </li>
  ))

  const total = data?.composePageArticleCollection?.total ?? 1
  const limit = data?.composePageArticleCollection?.limit ?? 1
  const pageCount = Math.ceil(total / limit)

  const [featuredArticle] =
    data?.composeComponentFeaturedArticleCollection?.items
      .filter(notEmpty)
      .map((articleListItemData) => articleListItemData.featuredArticle)
      .filter(notEmpty)
      .map((articleListItemData) => (
        <li key={articleListItemData.sys.id}>
          <FeaturedArticleListItem
            data={articleListItemData}
            substitutions={substitutions}
          />{' '}
        </li>
      )) ?? []

  const topicList = data?.composeComponentTopicListCollection?.items[0]

  const popularArticles =
    data?.composeComponentPopularArticlesCollection?.items[0]?.articlesCollection?.items
      .filter(notEmpty)
      .map((articleListItemData) => (
        <li
          key={articleListItemData.sys.id}
          className="bg-white hover:shadow-[0px_0px_10px_rgba(0,0,0,0.16)]"
        >
          <ArticleListItem
            data={articleListItemData}
            substitutions={substitutions}
          />{' '}
        </li>
      )) ?? []

  let title = 'Reading Eggs Articles'
  const metaDescription =
    data?.composePageArticlesIndexPageCollection?.items[0]?.seoDescription ??
    'All the latest articles from Reading Eggs.'
  if (pageNumber > 1) title = `${title}, Page ${pageNumber.toString()}`

  const pathname =
    pageNumber > 1 ? `/articles/page-${pageNumber.toString()}/` : '/articles/'
  const localizedVersions = buildLocalizedVersions(pathname)

  return (
    <>
      <Head>
        <title>{title}</title>
        <meta name="description" content={metaDescription} />
        <link
          rel="alternate"
          hrefLang="x-default"
          href={getXDefaultHref(pathname)}
        />
        {localizedVersions.map(({ locale, href }) => (
          <link key={locale} rel="alternate" hrefLang={locale} href={href} />
        ))}
        <link rel="canonical" href={buildURLForLocale(pathname, siteLocale)} />
      </Head>
      <Container className="space-y-12 py-12">
        <>
          <h1 className={textStyles.h1}>Reading Eggs Articles</h1>
          {featuredArticle && <ul className="space-y-12">{featuredArticle}</ul>}

          <h2 className={textStyles.h2}>Latest Articles</h2>

          <ul className="space-y-12">{latestArticles}</ul>
          <div className="flex justify-center">
            <PaginationLinks
              pageCount={pageCount}
              currentPage={pageNumber}
              PaginationLinkComponent={ArticlePaginationLink}
              EllipsisComponent={PaginationEllipsis}
              PrevPageLinkComponent={ArticlePaginationPrevLink}
              NextPageLinkComponent={ArticlePaginationNextLink}
            />
          </div>
        </>
      </Container>
      <div className="bg-gray">
        {topicList && (
          <div className="mb-12 bg-slate-50 py-16">
            <Container>
              <>
                <h2 className={`${textStyles.h2} mb-6 self-start`}>
                  Discover Topics
                </h2>
                <ComposeComponentTopicList data={topicList} />
              </>
            </Container>
          </div>
        )}
      </div>
      {popularArticles.length > 0 && (
        <Container className="mb-24 space-y-12">
          <>
            <h2 className={textStyles.h2}>Popular Articles</h2>

            <ul className="space-y-12">{popularArticles}</ul>
          </>
        </Container>
      )}
    </>
  )
}

export const getStaticPathsForArticleIndexPage: GetStaticPaths = async () => {
  const result = await graphQLClient
    .query(ArticlesQueryDocument, {
      limit: 1,
      skip: 0,
      preview: false,
      locale: siteLocale,
    })
    .toPromise()
  const itemCount = result.data?.composePageArticleCollection?.total ?? 1
  const pageCount = calculatePageCount(itemCount, pageSize)
  const paths = Array.from({ length: pageCount }, (_x, i) => i + 1).map(
    (pageNumber) => ({
      params: { slug: `page-${pageNumber.toString()}` },
    }),
  )
  return { paths, fallback: false }
}

export const getStaticPropsForArticleIndexPage: GetStaticProps<
  ArticleIndexPageProps
> = async ({ params, preview }) => {
  let pageNumberParam = 1
  const slug = params?.['slug']
  if (typeof slug === 'string') {
    const pageNumberFromSlug = extractPageNumberFromParam(slug)
    if (typeof pageNumberFromSlug === 'number') {
      pageNumberParam = pageNumberFromSlug
    }
  }

  const skip = pageSize * (pageNumberParam - 1)
  const limit = pageSize

  const result = await getGraphQLClient(preview)
    .query(ArticlesQueryDocument, {
      limit,
      skip,
      preview: !!preview,
      locale: siteLocale,
    })
    .toPromise()

  const substitutions: Substitutions =
    await fetchGlobalRegionalEditsAsSubstitutions()

  if (typeof result.data === 'undefined') {
    return {
      props: {
        kind: 'ArticleIndexPageProps',
        data: null,
        substitutions,
        pageNumber: pageNumberParam,
      },
      revalidate: ARTICLES_PAGES_REVALIDATE_SECONDS,
    }
  }

  return {
    props: {
      kind: 'ArticleIndexPageProps',
      data: result.data,
      substitutions,
      pageNumber: pageNumberParam,
    },
    revalidate: ARTICLES_PAGES_REVALIDATE_SECONDS,
  }
}
